import { useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useBoolean, useSetState } from 'react-use'
import clsx from 'clsx'
import { Basic } from 'unsplash-js/dist/methods/photos/types'

import { ImageArrayValue } from '@makyoapp/makyo-common'
import { Button, ButtonVariant, SimpleInput, Tabs } from '@makyoapp/makyo-ui'

import { unsplash } from '@/lib/unsplash'
import { ImageManagerKind, useImageManagerContext } from '@/context/ImageManagerContext'
import { LazyImage } from '@/components/LazyImage'

interface Params {
  search: string
  page: number
}

export function useLoadItems(
  params: Params,
  setParams: (
    patch:
      | Partial<{ page: number; search: string }>
      | ((prevState: { page: number; search: string }) => Partial<{ page: number; search: string }>)
  ) => void
) {
  const [isLoading, setIsLoading] = useBoolean(false)
  const [items, setItems] = useState<Basic[]>([])
  const [hasNextPage, setHasNextPage] = useBoolean(true)
  const [error, setError] = useBoolean(false)

  /*
  useEffect(() => {
    if (!search) {
      setItems([])
    }
  }, [setItems, search])
   */

  const loadMore = async () => {
    if (isLoading) {
      return
    }

    setIsLoading(true)

    try {
      const res = await unsplash.search.getPhotos({
        query: params.search,
        page: params.page,
        perPage: 6,
        orderBy: 'relevant',
      })

      if (res.response) {
        const items = res.response.results || []
        const total = res.response.total_pages
        setItems((current) => [...current, ...items])
        setParams((prev) => {
          return {
            page: prev.page + 1,
          }
        })
        setHasNextPage(params.page < total)
      }
    } catch (err) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return { isLoading, items, hasNextPage, error, setItems, loadMore }
}

const UNSPLASH_KEYWORDS = [
  'abstract',
  'animal',
  'architecture',
  'art',
  'business',
  'colorful',
  'food',
  'interior',
  'minimal',
  'nature',
  'plant',
  'portrait',
  'space',
  'technology',
  'texture',
  'wallpaper',
]

export function UnsplashPanel() {
  const [search, setSearch] = useState('')
  const [params, setParams] = useSetState({
    page: 1,
    search: '',
  })

  const { onHide, onChange, id, multiple, selectedImages, setSelectedImages, maxHeight } = useImageManagerContext()

  const { items, hasNextPage, error, isLoading, loadMore, setItems } = useLoadItems(params, setParams)

  const [infiniteRef, { rootRef }] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: loadMore,
    // When there is an error, we stop infinite loading.
    // It can be reactivated by setting "error" state as undefined.
    disabled: !!error,
    // `rootMargin` is passed to `IntersectionObserver`.
    // We can use it to trigger 'onLoadMore' when the sentry comes near to become
    // visible, instead of becoming fully visible on the screen.
    rootMargin: '0px 0px 600px 0px',
  })

  const isUnsplashUrl = (url: string) => {
    return items.some((item) => url.includes(item.urls.raw))
  }

  //const [selected, setSelected] = useState<ImageArrayValue[]>([])

  /*
  useEffect(() => {
    if (multiple) {
      setSelectedImages(selected.map((s) => s.Url))
    }
  }, [selected, multiple, setSelectedImages])
*/

  const handleClear = () => {
    setSelectedImages(() => [])
  }

  // track initial selected images
  const initialSelectedRef = useRef(selectedImages)

  return (
    <Tabs.Panel data-panel-id={ImageManagerKind.StockPhotos} eventKey={ImageManagerKind.StockPhotos}>
      <div className='flex-c pb-4'>
        <SimpleInput
          autoFocus
          id={id ? `${id}-unsplash` : undefined}
          inputClassName='mk-im-unsplash-input'
          showClear={Boolean(search)}
          onClear={() => {
            setItems([])
            setSearch('')
            setParams({
              page: 1,
              search: '',
            })
          }}
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation()
              if (Boolean(search)) {
                setItems([])
                setParams({ search, page: 1 })
              }
            }
          }}
        />
        <Button
          onClick={() => {
            setItems([])
            setParams({ search, page: 1 })
          }}
          disabled={!Boolean(search)}
        >
          <Trans>Search</Trans>
        </Button>
      </div>
      {items.length < 1 && (
        <div className='flex gap-2 flex-wrap py-4'>
          {UNSPLASH_KEYWORDS.map((kw) => (
            <Button
              onClick={() => {
                setItems([])
                setSearch(kw)
                setParams({
                  search: kw,
                  page: 1,
                })
              }}
              noPadding
              defaultSize={false}
              defaultFontStyle={false}
              className='py-1 px-2'
              key={kw}
              variant={ButtonVariant.OutlinedGray}
            >
              {kw}
            </Button>
          ))}
        </div>
      )}
      <div
        ref={rootRef}
        className='overflow-y-auto'
        style={{
          maxHeight: maxHeight ? maxHeight : multiple ? '50vh' : '60vh',
        }}
      >
        <div className='grid grid-cols-3 gap-4' style={{ maxHeight: '20vh' }}>
          {items.map((item, idx) => {
            const isSelected = multiple ? selectedImages.find((s) => s.Url === item.urls.raw) : false
            return (
              <button
                onClick={() => {
                  if (!multiple) {
                    onChange(ImageManagerKind.StockPhotos, item.urls.raw)
                    onHide?.()
                  } else {
                    if (isSelected) {
                      setSelectedImages((prev) => {
                        return prev.filter((p) => p.Url !== item.urls.raw)
                      })
                    } else {
                      setSelectedImages((prev) => {
                        return [
                          ...prev,
                          {
                            Url: item.urls.raw,
                            Format: '',
                            Height: item.height,
                            Width: item.width,
                            CreatedAt: new Date(item.created_at),
                            Tags: [],
                            Title: item.description ?? item.alt_description ?? '',
                            Size: 0,
                            Owner: item.user.name,
                            Description: item.description ?? item.alt_description ?? '',
                          } as ImageArrayValue,
                        ]
                      })
                    }
                  }
                }}
                key={idx}
                className={clsx('mk-im-img mb-0!', isSelected && 'selected')}
              >
                <img className='min-w-full min-h-[40px] w-full' src={`${item.urls.raw}&q=50&w=320`} />
              </button>
            )
          })}
        </div>
        {isLoading && (
          <div className='flex-cc h-10'>
            <span className='loading spin' />
          </div>
        )}
        {params.search && <div ref={infiniteRef} className='w-full h-2' />}
      </div>
      {multiple && (
        <div
          className='flex-col justify-end px-4 pt-4 border-t bdr mt-4'
          style={{
            marginInlineStart: '-1rem',
            width: 'calc(100% + 2rem)',
          }}
        >
          <div className='py-2 w-full'>
            <div className='flex items-center'>
              <div className='text-xs text-gray-500 dark:text-gray-400 mr-3'>
                {selectedImages.length} {selectedImages.length === 1 ? 'image' : 'images'} selected
              </div>

              {selectedImages.length > 0 && (
                <div className='overflow-x-auto max-w-md' style={{ scrollbarWidth: 'thin' }}>
                  <div className='flex flex-nowrap space-x-2 py-2 pr-2' style={{ width: 'max-content' }}>
                    {selectedImages.map((image) => (
                      <div key={image.Url} className='relative group shrink-0'>
                        <div className='w-8 h-8 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-sm overflow-hidden'>
                          <div className='w-full h-full flex items-center justify-center'>
                            <LazyImage
                              src={`${image.Url}&q=50&w=80`}
                              failSrc={image.Url}
                              alt={image.Title || 'Selected image'}
                            />
                          </div>
                        </div>
                        <button
                          className='absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center text-xs'
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedImages((prev) => prev.filter((p) => p.Url !== image.Url))
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='flex items-center w-full'>
            <Button
              variant={ButtonVariant.Outlined}
              onClick={() => {
                onChange(ImageManagerKind.MyImages, initialSelectedRef.current)
                onHide?.()
              }}
              className='mr-2'
            >
              <Trans>Cancel</Trans>
            </Button>

            <Button variant={ButtonVariant.Outlined} onClick={handleClear} className='w-20 mr-auto'>
              <Trans>Clear</Trans>
            </Button>

            <Button
              className='w-20'
              onClick={() => {
                onChange(ImageManagerKind.StockPhotos, selectedImages)
                onHide?.()
              }}
            >
              <Trans>Save</Trans>
            </Button>
          </div>
        </div>
      )}
    </Tabs.Panel>
  )
}
